import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import clsx from 'clsx';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import FormPageLayout from '../../components/ui/FormPageLayout';
import FilterSelect from '../../components/ui/FilterSelect';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import { fetchFormationEnrollmentBundle } from '../../utils/formationEnrollmentBundle';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import {
  FORMATION_PAYMENTS_QUERY_KEY,
  FORMATION_STUDENTS_CATALOG_KEY,
  SPA_COURSES_KEY,
  FORMATION_ENROLLMENT_BUNDLE_KEY,
} from '../../utils/queryKeys';
import { formationAPI, spaEntityAPI } from '../../services/api';
import { fetchOrganizationSettings, ORGANIZATION_SETTINGS_KEY } from '../../services/settingsAPI';
import {
  generateInvoiceReference,
  computeDueDate,
  resolveBillingSettings,
  isPaymentOverdue,
} from '../../utils/billingHelpers';
import toast from 'react-hot-toast';
import { confirmDialog } from '../../utils/confirmBridge';
import notify from '../../utils/notify';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  PrinterIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const PAYMENT_METHOD_TO_API = {
  Espèces: 'cash',
  'Carte bancaire': 'card',
  'Virement bancaire': 'transfer',
  Chèque: 'check',
};

const PAYMENT_METHOD_FROM_API = {
  cash: 'Espèces',
  card: 'Carte bancaire',
  transfer: 'Virement bancaire',
  check: 'Chèque',
};

/** Map FR label or API-ish code → paymentsPageUi key suffix */
const METHOD_DISPLAY_KEYS = {
  cash: 'methodCash',
  Espèces: 'methodCash',
  card: 'methodCardBank',
  'carte bancaire': 'methodCardBank',
  'Carte bancaire': 'methodCardBank',
  transfer: 'methodTransferBank',
  Transfer: 'methodTransferBank',
  'virement bancaire': 'methodTransferBank',
  'Virement bancaire': 'methodTransferBank',
  check: 'methodCheck',
  Chèque: 'methodCheck',
  cheque: 'methodCheck',
};

function localizedPaymentMethodLabel(methodDisplay, apiCode, t) {
  const fromApi =
    apiCode != null ? PAYMENT_METHOD_FROM_API[String(apiCode).toLowerCase()] || PAYMENT_METHOD_FROM_API[apiCode] : '';
  const raw = methodDisplay || fromApi || apiCode || '';
  const suffix =
    METHOD_DISPLAY_KEYS[raw] ||
    METHOD_DISPLAY_KEYS[String(raw).toLowerCase()];
  return suffix ? t(`paymentsPageUi.${suffix}`) : raw ? String(raw) : t('paymentsPageUi.undef');
}

function newFormationPaymentId() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `pay-${crypto.randomUUID()}`
    : `pay-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function normalizeFormationPayment(row) {
  if (!row) return row;
  let legacy = {};
  if (row.legacyPayloadJson) {
    try {
      legacy =
        typeof row.legacyPayloadJson === 'string'
          ? JSON.parse(row.legacyPayloadJson)
          : row.legacyPayloadJson;
    } catch (_) {
      /* ignore */
    }
  }
  return {
    ...legacy,
    ...row,
    isPaid: row.isPaid === true || row.isPaid === 1,
    method: legacy.method || PAYMENT_METHOD_FROM_API[row.paymentMethod] || row.paymentMethod || row.method,
    reference: legacy.reference || row.invoiceNumber || row.reference,
    paymentDate: row.date || legacy.paymentDate || legacy.date,
    date: row.date || legacy.date,
    totalAmount: row.totalAmount != null ? Number(row.totalAmount) : legacy.totalAmount,
    paidAmount: row.paidAmount != null ? Number(row.paidAmount) : legacy.paidAmount,
    amount: row.amount != null ? Number(row.amount) : legacy.amount,
    student: legacy.student || row.student,
    course: legacy.course || row.course,
    batch: legacy.batch || row.batch,
    member: legacy.member || row.member,
    dueDate: legacy.dueDate || row.dueDate || null,
    remainingAmount:
      legacy.remainingAmount != null
        ? Number(legacy.remainingAmount)
        : Math.max(
            0,
            (row.totalAmount != null ? Number(row.totalAmount) : Number(legacy.totalAmount) || 0) -
              (row.paidAmount != null ? Number(row.paidAmount) : Number(legacy.paidAmount) || 0)
          ),
  };
}

function buildFormationPaymentPayload(paymentToSave, existingPayment, isEditing) {
  const totalAmount = parseFloat(paymentToSave.totalAmount) || 0;
  const paidAmount = parseFloat(paymentToSave.paidAmount) || 0;
  const isPaid = Boolean(paymentToSave.isPaid);
  const { student, course, batch, member, ...rest } = paymentToSave;
  const legacyPayload = {
    ...rest,
    student,
    course,
    batch,
    member,
    reference: paymentToSave.reference,
    method: paymentToSave.method,
    batchId: paymentToSave.batchId,
    enrollmentId: paymentToSave.enrollmentId,
    installments: paymentToSave.installments,
    dueDate: isPaid ? null : paymentToSave.dueDate || null,
    remainingAmount: isPaid ? 0 : Math.max(0, totalAmount - paidAmount),
  };

  return {
    id: isEditing && existingPayment?.id ? existingPayment.id : newFormationPaymentId(),
    studentId: paymentToSave.studentId || '',
    invoiceNumber: paymentToSave.reference || '',
    totalAmount,
    paidAmount,
    amount: paidAmount,
    status: isPaid ? 'paid' : 'partial',
    isPaid,
    paymentMethod: PAYMENT_METHOD_TO_API[paymentToSave.method] || paymentToSave.method || 'cash',
    date: paymentToSave.date,
    courseId: course?.id || paymentToSave.courseId || '',
    notes: paymentToSave.notes || '',
    legacyPayloadJson: JSON.stringify(legacyPayload),
  };
}

const PaymentDetail = ({ payment, students = [] }) => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'ar' ? 'ar-DZ' : 'fr-FR';

  const getStudentName = (pmt) => {
    if (pmt.student?.name) return pmt.student.name;
    if (pmt.member?.firstName && pmt.member?.lastName) {
      return `${pmt.member.firstName} ${pmt.member.lastName}`.trim();
    }
    const studentId = pmt.studentId || pmt.memberId;
    if (studentId && students.length > 0) {
      const student = students.find(
        (s) =>
          s.id === studentId ||
          s.memberCode === studentId ||
          s.studentCode === studentId
      );
      if (student) {
        return student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim();
      }
    }
    return t('paymentsPageUi.studentFallbackName');
  };

  const safe = (val) => val ?? '';

  if (!payment) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6 text-center text-slate-600 dark:text-slate-400">
        <p>{t('paymentsPageUi.paymentDetailMissing')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <div className="mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-1">
            {t('paymentsPageUi.invoicePrefix')}
            {safe(payment.invoiceNumber || payment.reference)}
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm">
            {t('paymentsPageUi.issuedOnLbl')}{' '}
            {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString(dateLocale) : '-'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <h3 className="text-sm text-slate-600 dark:text-slate-400 font-medium mb-1">
            {t('paymentsPageUi.clientLbl')}
          </h3>
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-forma-500/15 flex items-center justify-center mr-3">
              <AcademicCapIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
            </div>
            <div>
              <p className="text-slate-900 dark:text-slate-50 font-medium">{getStudentName(payment)}</p>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                {payment.member?.memberCode ||
                  payment.student?.code ||
                  payment.student?.memberCode ||
                  payment.student?.studentCode ||
                  t('paymentsPageUi.undef')}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <h3 className="text-sm text-slate-600 dark:text-slate-400 font-medium mb-1">
            {t('paymentsPageUi.courseSectionLbl')}
          </h3>
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center mr-3">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-300" />
            </div>
            <div>
              <p className="text-slate-900 dark:text-slate-50 font-medium">
                {payment.course?.title || payment.description || t('paymentsPageUi.noCourseChosen')}
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                {payment.course?.code || payment.category || t('paymentsPageUi.undef')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">
          {t('paymentsPageUi.paymentDetailsLbl')}
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.courseTotalLbl')}</span>
            <span className="text-slate-900 dark:text-slate-50 font-medium">
              {(payment.totalAmount ?? payment.amount ?? 0).toLocaleString(dateLocale)} DA
            </span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.paidAmountLbl')}</span>
            <span className="text-green-400 font-medium">
              {(payment.paidAmount ?? payment.amount ?? 0).toLocaleString(dateLocale)} DA
            </span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.remainingLbl')}</span>
            <span
              className={`font-medium ${
                (payment.totalAmount ?? payment.amount ?? 0) - (payment.paidAmount ?? payment.amount ?? 0) > 0
                  ? 'text-red-400'
                  : 'text-green-400'
              }`}
            >
              {Math.max(
                0,
                (payment.totalAmount ?? payment.amount ?? 0) - (payment.paidAmount ?? payment.amount ?? 0)
              ).toLocaleString(dateLocale)}{' '}
              DA
            </span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
            <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.paymentMethodLbl')}</span>
            <span className="text-slate-900 dark:text-slate-50 font-medium">
              {localizedPaymentMethodLabel(payment.method, payment.paymentMethod, t)}
            </span>
          </div>
          {!payment.isPaid && payment.dueDate ? (
            <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600">
              <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.dueDateLbl')}</span>
              <div className="flex items-center gap-2">
                <span className="text-slate-900 dark:text-slate-50 font-medium">
                  {new Date(payment.dueDate).toLocaleDateString(dateLocale)}
                </span>
                {isPaymentOverdue(payment) ? (
                  <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-300">
                    {t('paymentsPageUi.overdueBadge')}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="flex justify-between items-center">
            <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.lblStatusPayment')}</span>
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                payment.isPaid ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
              }`}
            >
              {payment.isPaid ? t('paymentsPageUi.paidLbl') : t('paymentsPageUi.partialLbl')}
            </span>
          </div>
        </div>
      </div>

      {payment.installments && payment.installments.length > 0 && (
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">
            {t('paymentsPageUi.installmentsHeading')}
          </h3>
          <div className="space-y-3">
            {payment.installments.map((installment, index) => (
              <div
                key={index}
                className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-600"
              >
                <div>
                  <span className="text-slate-600 dark:text-slate-400">
                    {t('paymentsPageUi.installmentNth', { n: index + 1 })}
                  </span>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {t('paymentsPageUi.installmentDatePrefix')}{' '}
                    {new Date(installment.dueDate).toLocaleDateString(dateLocale)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-900 dark:text-slate-50 font-medium">
                    {installment.amount.toLocaleString(dateLocale)} DA
                  </span>
                  {installment.isPaid ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PaymentForm = ({ isEditing = false, paymentData = null, students = [], courses = [], existingPayments = [] }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const dateLocale = language === 'ar' ? 'ar-DZ' : 'fr-FR';
  const initialPartial =
    paymentData && (!paymentData.isPaid || paymentData.installments?.length > 0);
  const [paymentType, setPaymentType] = useState(initialPartial ? 'partial' : 'full');
  const [dueDateTouched, setDueDateTouched] = useState(Boolean(paymentData?.dueDate));

  const { data: orgSettings } = useQuery(
    ORGANIZATION_SETTINGS_KEY,
    fetchOrganizationSettings,
    { staleTime: 60_000 }
  );

  const billing = resolveBillingSettings(orgSettings);
  
  const [formData, setFormData] = useState({
    studentId: paymentData?.student?.id || paymentData?.studentId || '',
    courseId: paymentData?.course?.id || '',
    totalAmount: paymentData?.totalAmount || '',
    paidAmount: paymentData?.paidAmount || '',
    date: paymentData?.date ? new Date(paymentData.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    dueDate: paymentData?.dueDate ? new Date(paymentData.dueDate).toISOString().split('T')[0] : '',
    method: paymentData?.method || 'Espèces',
    notes: paymentData?.notes || '',
    reference: paymentData?.reference || ''
  });

  useEffect(() => {
    if (!isEditing && !paymentData?.reference && orgSettings) {
      const reference = generateInvoiceReference(orgSettings.billing, existingPayments);
      setFormData((prev) => (prev.reference ? prev : { ...prev, reference }));
    }
  }, [orgSettings, existingPayments, isEditing, paymentData?.reference]);

  useEffect(() => {
    if (paymentType !== 'partial' || dueDateTouched) return;
    const computed = computeDueDate(formData.date, billing.paymentTerms);
    if (computed && computed !== formData.dueDate) {
      setFormData((prev) => ({ ...prev, dueDate: computed }));
    }
  }, [paymentType, formData.date, billing.paymentTerms, dueDateTouched, formData.dueDate]);
  
  const { data: enrollmentBundle } = useQuery(
    FORMATION_ENROLLMENT_BUNDLE_KEY,
    fetchFormationEnrollmentBundle,
    { staleTime: 15_000, refetchOnWindowFocus: true }
  );

  const realCourses = courses || [];
  const realStudents = students || [];
  
  const getAvailableBatchesForStudent = (studentId) => {
    if (!studentId || !enrollmentBundle) return [];

    try {
      const enrollments = (enrollmentBundle.enrollments || []).filter(
        (e) => e.studentId === studentId && e.status === 'active'
      );
      const batchById = {};
      (enrollmentBundle.batches || []).forEach((b) => {
        if (b?.id) batchById[b.id] = b;
      });

      return enrollments
        .map((enrollment) => {
          const batch = batchById[enrollment.batchId];
          if (!batch) return null;
          return {
            ...batch,
            enrollmentId: enrollment.id,
            enrollmentDate: enrollment.enrollmentDate,
            paymentStatus: enrollment.paymentStatus || 'pending',
          };
        })
        .filter(Boolean);
    } catch (error) {
      console.error('Error getting student batches:', error);
      return [];
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (name === 'dueDate') {
      setDueDateTouched(true);
    }
    
    // Auto-fill batch and amount when student is selected
    if (name === 'studentId' && value) {
      const safeStudents = Array.isArray(realStudents) ? realStudents : [];
      const selectedStudent = safeStudents.find(s => s.id === value);
      
      // Clear previous selections when student changes
      setFormData(prev => ({
        ...prev,
        [name]: value,
        batchId: '',
        enrollmentId: '',
        totalAmount: 0,
        paidAmount: 0
      }));
      
      const studentBatches = getAvailableBatchesForStudent(value);
      if (studentBatches.length > 0) {
        toast.success(
          t('paymentsPageUi.toastStudentEnrollFound', {
            n: studentBatches.length,
            name: selectedStudent?.name || '',
          }),
          {
            duration: 2000,
            position: 'top-right',
          }
        );
      } else {
        toast.info(t('paymentsPageUi.toastNoActiveEnrollmentStudent', { name: selectedStudent?.name || '' }), {
          duration: 2000,
          position: 'top-right',
        });
      }
    }
    // Auto-fill total amount when batch enrollment is selected
    else if (name === 'batchId' && value) {
      const studentBatches = getAvailableBatchesForStudent(formData.studentId);
      const selectedBatch = studentBatches.find(batch => batch.id === value);
      
      if (selectedBatch) {
        // Get course details to find price
        const safeCourses = Array.isArray(realCourses) ? realCourses : [];
        const course = safeCourses.find(c => c.id === selectedBatch.courseId);
        const coursePrice = parseFloat(course?.price) || 0;
        
        setFormData(prev => ({
          ...prev,
          [name]: value,
          enrollmentId: selectedBatch.enrollmentId,
          totalAmount: coursePrice,
          paidAmount: paymentType === 'full' ? coursePrice : prev.paidAmount
        }));
        
        toast.success(
          t('paymentsPageUi.toastBatchSelectedPrice', {
            batch: selectedBatch.batchName || '',
            price: coursePrice.toLocaleString(dateLocale),
          }),
          {
            duration: 2000,
            position: 'top-right',
          }
        );
      }
    } else {
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!formData.studentId) {
        toast.error(t('paymentsPageUi.toastSelectStudent'));
        return;
      }

      if (!formData.batchId) {
        toast.error(t('paymentsPageUi.toastSelectEnrollment'));
        return;
      }

      if (!formData.totalAmount) {
        toast.error(t('paymentsPageUi.toastTotalAmountRequired'));
        return;
      }
      
      // Prepare payment data
      let paymentToSave = {
        ...formData,
        paidAmount: paymentType === 'full' ? formData.totalAmount : formData.paidAmount,
        isPaid: paymentType === 'full' || parseFloat(formData.totalAmount) === parseFloat(formData.paidAmount),
        remainingAmount: paymentType === 'partial' ? parseFloat(formData.totalAmount) - parseFloat(formData.paidAmount) : 0,
        dueDate: paymentType === 'partial' ? formData.dueDate : null,
      };
      
      // Look up actual student and batch info from real data
      console.log('Looking for student ID:', formData.studentId);
      console.log('Looking for batch ID:', formData.batchId);
      console.log('Looking for enrollment ID:', formData.enrollmentId);
      
      const selectedStudent = realStudents.find(s => s.id === formData.studentId);
      const studentBatches = getAvailableBatchesForStudent(formData.studentId);
      const selectedBatch = studentBatches.find(b => b.id === formData.batchId);
      const selectedCourse = selectedBatch ? realCourses.find(c => c.id === selectedBatch.courseId) : null;
      
      console.log('Found student:', selectedStudent);
      console.log('Found batch:', selectedBatch);
      console.log('Found course:', selectedCourse);
      
      // Add batch and enrollment information to payment
      if (selectedBatch) {
        paymentToSave.batch = {
          id: selectedBatch.id,
          name: selectedBatch.batchName,
          courseName: selectedBatch.courseName,
          days: selectedBatch.days,
          startTime: selectedBatch.startTime,
          endTime: selectedBatch.endTime,
          trainerName: selectedBatch.trainerName
        };
        paymentToSave.batchId = selectedBatch.id;
        paymentToSave.enrollmentId = formData.enrollmentId;
      }
      
      // Always assign student info (using member data structure)
      if (selectedStudent) {
        const studentName = selectedStudent.name || 
                           `${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim() ||
                           selectedStudent.fullName ||
                           `Étudiant-${selectedStudent.id}`;
        
        paymentToSave.student = {
          id: selectedStudent.id,
          name: studentName,
          firstName: selectedStudent.firstName,
          lastName: selectedStudent.lastName,
          code: selectedStudent.memberCode || selectedStudent.code || selectedStudent.studentCode || 'N/A'
        };
        // Also store member reference for consistency
        paymentToSave.member = selectedStudent;
        paymentToSave.studentId = selectedStudent.id;
        console.log('Assigned student to payment:', paymentToSave.student);
      } else {
        console.log('No student found for ID:', formData.studentId);
        paymentToSave.student = {
          id: formData.studentId,
          name: t('paymentsPageUi.studentMissingPlaceholder'),
          firstName: t('paymentsPageUi.studentMissingPlaceholder'),
          lastName: '',
          code: t('paymentsPageUi.undef'),
        };
        paymentToSave.studentId = formData.studentId;
      }
      
      // Always assign course info using real course data structure
      if (selectedCourse) {
        paymentToSave.course = {
          id: selectedCourse.id,
          title: selectedCourse.title || selectedCourse.name,
          code: selectedCourse.code || selectedCourse.id
        };
        console.log('Assigned course to payment:', paymentToSave.course);
      } else {
        console.log('No course found for ID:', formData.courseId);
        paymentToSave.course = {
          id: formData.courseId,
          title: t('paymentsPageUi.courseNotFoundTitle'),
          code: t('paymentsPageUi.undef'),
        };
      }
      
      console.log('Final payment object before saving:', paymentToSave);
      
      // Validate partial payment amount
      if (paymentType === 'partial') {
        const totalAmount = parseFloat(formData.totalAmount);
        const paidAmount = parseFloat(formData.paidAmount);
        
        if (paidAmount > totalAmount) {
          notify.show(t('paymentsPageUi.amountPaidGtTotal'));
          return;
        }

        if (paidAmount <= 0) {
          notify.show(t('paymentsPageUi.amountPaidMustBeGt0'));
          return;
        }

        if (!formData.dueDate) {
          notify.show(t('paymentsPageUi.toastDueDateRequired'));
          return;
        }
      }
      
      if (isEditing && paymentData) {
        const payload = buildFormationPaymentPayload(paymentToSave, paymentData, true);
        await formationAPI.updateFormationPayment(paymentData.id, payload);
      } else {
        const payload = buildFormationPaymentPayload(paymentToSave, null, false);
        await formationAPI.createFormationPayment(payload);
      }
      
      await invalidateFormationGraph(queryClient);
      
      navigate('/payments');
    } catch (error) {
      console.error('Error saving payment data:', error);
      toast.error(t('paymentsPageUi.saveErrorToast'));
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
        {isEditing ? t('paymentsPageUi.formTitleEditPayment') : t('paymentsPageUi.formTitleNewPayment')}
      </h2>

      {(realStudents.length === 0 || realCourses.length === 0) && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-center text-yellow-300">
            <ArrowPathIcon className="w-5 h-5 mr-2" />
            {realStudents.length === 0 && realCourses.length === 0
              ? t('paymentsPageUi.helperNoStudentOrCourse')
              : realStudents.length === 0
                ? t('paymentsPageUi.helperNoStudent')
                : t('paymentsPageUi.helperNoCourses')}
          </div>
        </div>
      )}
      
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('paymentsPageUi.lblStudentField')}
            </label>
            <select
              name="studentId"
              value={formData.studentId}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
              disabled={realStudents.length === 0}
            >
              <option value="">
                {realStudents.length === 0
                  ? t('paymentsPageUi.noStudentsAvail')
                  : t('paymentsPageUi.selectStudentPlaceholder')}
              </option>
              {(Array.isArray(realStudents) ? realStudents : []).map((student) => (
                <option key={student.id} value={student.id}>
                  {student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim()}
                  {student.currentCourse &&
                    ` • ${typeof student.currentCourse === 'string' ? t('paymentsPageUi.courseAssignedShort') : student.currentCourse.title}`}
                  ({student.memberCode || student.code || student.id})
                </option>
              ))}
            </select>
            {realStudents.length > 0 && (
              <div className="text-green-400 text-xs mt-1 flex items-center">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                {t('paymentsPageUi.studentCountAvail', { n: realStudents.length })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('paymentsPageUi.lblEnrollmentField')}
            </label>
            <select
              name="batchId"
              value={formData.batchId || ''}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
              disabled={!formData.studentId}
            >
              <option value="">
                {!formData.studentId ? t('paymentsPageUi.pickStudentFirstHint') : t('paymentsPageUi.selectBatchPlaceholder')}
              </option>
              {formData.studentId &&
                getAvailableBatchesForStudent(formData.studentId).map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.courseName} - {batch.batchName} ({batch.days.join(', ')} • {batch.startTime}-{batch.endTime})
                    {batch.paymentStatus === 'paid'
                      ? t('paymentsPageUi.badgePaidSuffix')
                      : batch.paymentStatus === 'partial'
                        ? t('paymentsPageUi.badgePartialSuffix')
                        : t('paymentsPageUi.badgePendingSuffix')}
                  </option>
                ))}
            </select>
            {formData.studentId && (
              <div className="text-blue-400 text-xs mt-1 flex items-center">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                {t('paymentsPageUi.enrollmentCountBanner', {
                  n: getAvailableBatchesForStudent(formData.studentId).length,
                })}
              </div>
            )}
            {!formData.studentId && (
              <div className="text-orange-400 text-xs mt-1">{t('paymentsPageUi.selectStudentFirstBanner')}</div>
            )}
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('paymentsPageUi.lblTotalAmountField')}
            </label>
            <input
              type="number"
              name="totalAmount"
              value={formData.totalAmount}
              readOnly
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-600 dark:text-slate-400 cursor-not-allowed"
              placeholder={t('paymentsPageUi.placeholderCoursePrice')}
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('paymentsPageUi.lblDateField')}
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('paymentsPageUi.lblPaymentKind')}
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentType"
                  value="full"
                  checked={paymentType === 'full'}
                  onChange={() => {
                    setPaymentType('full');
                    setDueDateTouched(false);
                    setFormData((prev) => ({ ...prev, paidAmount: prev.totalAmount, dueDate: '' }));
                  }}
                  className="text-forma-600 focus:ring-forma-500"
                />
                <span className="text-slate-900 dark:text-slate-50">{t('paymentsPageUi.paymentKindFull')}</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentType"
                  value="partial"
                  checked={paymentType === 'partial'}
                  onChange={() => {
                    setPaymentType('partial');
                    setDueDateTouched(false);
                    const computed = computeDueDate(formData.date, billing.paymentTerms);
                    setFormData((prev) => ({ ...prev, paidAmount: 0, dueDate: computed }));
                  }}
                  className="text-forma-600 focus:ring-forma-500"
                />
                <span className="text-slate-900 dark:text-slate-50">{t('paymentsPageUi.paymentKindPartial')}</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('paymentsPageUi.paymentMethodLbl')}
            </label>
            <select
              name="method"
              value={formData.method}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            >
              <option value="Espèces">{t('paymentsPageUi.methodCash')}</option>
              <option value="Carte bancaire">{t('paymentsPageUi.methodCardBank')}</option>
              <option value="Virement bancaire">{t('paymentsPageUi.methodTransferBank')}</option>
              <option value="Chèque">{t('paymentsPageUi.methodCheck')}</option>
            </select>
          </div>

          {paymentType === 'partial' && (
            <>
            <div className="md:col-span-2">
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('paymentsPageUi.lblPaidTodayField')}
              </label>
              <input
                type="number"
                name="paidAmount"
                value={formData.paidAmount}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                required
                min="0"
                max={formData.totalAmount}
                placeholder={t('paymentsPageUi.placeholderAmountPaid')}
              />
              {formData.totalAmount && formData.paidAmount !== '' && (
                <div className="mt-2 p-3 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-600">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.labelMontantTotalShort')}</span>
                    <span className="text-slate-900 dark:text-slate-50 font-medium">
                      {parseFloat(formData.totalAmount).toLocaleString(dateLocale)} DA
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.labelMontantPayeShort')}</span>
                    <span className="text-green-400 font-medium">
                      {parseFloat(formData.paidAmount || 0).toLocaleString(dateLocale)} DA
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1 pt-2 border-t border-slate-200 dark:border-slate-600">
                    <span className="text-slate-600 dark:text-slate-400">{t('paymentsPageUi.labelMontantRestantShort')}</span>
                    <span className="text-red-400 font-medium">
                      {(parseFloat(formData.totalAmount) - parseFloat(formData.paidAmount || 0)).toLocaleString(dateLocale)} DA
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('paymentsPageUi.lblDueDateField')}
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                min={formData.date}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                required
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t('paymentsPageUi.dueDateHint', { days: billing.paymentTerms })}
              </p>
            </div>
            </>
          )}

          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('paymentsPageUi.lblRemarksField')}
            </label>
            <textarea
              rows="3"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              placeholder={t('paymentsPageUi.placeholderNotesSupplement')}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <Link
            to="/payments"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            {t('paymentsPageUi.cancelLink')}
          </Link>
          <button
            type="submit"
            className="px-4 py-2 rounded-lg font-medium text-white shadow bg-forma-600 hover:bg-forma-500 transition-all duration-200"
          >
            {isEditing ? t('paymentsPageUi.btnUpdate') : t('paymentsPageUi.btnSave')}
          </button>
        </div>
      </form>
    </div>
  );
};

// Component for payment detail route that uses useParams
const PaymentDetailRoute = ({ payments, students = [] }) => {
  const { id } = useParams();
  const { t } = useLanguage();
  const payment = payments.find((p) => p.id === id);
  
  return (
    <FormPageLayout backTo="/payments" backLabel={t('paymentsPageUi.backToPayments')}>
      <PaymentDetail payment={payment} students={students} />
    </FormPageLayout>
  );
};

// Component for payment edit route that uses useParams
const PaymentEditRoute = ({ payments, students, courses }) => {
  const { id } = useParams();
  const payment = payments.find(p => p.id === id);
  
  console.log('PaymentEditRoute: Looking for payment with ID:', id);
  console.log('PaymentEditRoute: Found payment:', payment);
  
  return (
    <PaymentForm 
      isEditing={true} 
      paymentData={payment}
      students={students}
      courses={courses}
      existingPayments={payments}
    />
  );
};

const PaymentsPage = () => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'ar' ? 'ar-DZ' : 'fr-FR';
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');

  // Remove local state - using React Query as single source of truth
  
  // Multi-select state
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  
  const { data: paymentsData } = useQuery(
    FORMATION_PAYMENTS_QUERY_KEY,
    async () => {
      try {
        const res = await formationAPI.getFormationPayments({});
        const items = Array.isArray(res?.items) ? res.items : [];
        return { payments: items.map(normalizeFormationPayment) };
    } catch (error) {
        console.error('Error fetching payments:', error);
        return { payments: [] };
      }
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );

  const { data: studentsData } = useQuery(
    FORMATION_STUDENTS_CATALOG_KEY,
    async () => {
      try {
        const res = await formationAPI.getStudents({ limit: 500, page: 1 });
        return res.items || [];
    } catch (error) {
        console.error('Error fetching students:', error);
        return [];
      }
    },
    {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );

  const { data: coursesData } = useQuery(
    SPA_COURSES_KEY,
    async () => {
      try {
        return await spaEntityAPI.list('courses');
      } catch (error) {
        console.error('Error fetching courses:', error);
        return [];
      }
    },
    {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );

  // Extract data from React Query  
  const payments = useMemo(() => paymentsData?.payments ?? [], [paymentsData?.payments]);
  const students = studentsData || [];
  const courses = coursesData || [];
  
  // Simple function to get student name for any payment
  const getStudentName = (payment) => {
    // Direct name from payment object
    if (payment.student?.name) return payment.student.name;
    if (payment.member?.firstName && payment.member?.lastName) {
      return `${payment.member.firstName} ${payment.member.lastName}`.trim();
    }
    
    // Find student by ID
    const studentId = payment.studentId || payment.memberId;
    if (studentId && students.length > 0) {
      const student = students.find(s => 
        s.id === studentId || 
        s.memberCode === studentId || 
        s.studentCode === studentId
      );
      if (student) {
        return student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim();
      }
    }
    
    return t('paymentsPageUi.studentFallbackName');
  };

  const { data: organizationInfo, isLoading: settingsLoading } = useQuery(
    ORGANIZATION_SETTINGS_KEY,
    fetchOrganizationSettings,
    { staleTime: 60_000, refetchOnWindowFocus: true }
  );
  
  // React Query handles data refreshing automatically
  
  // React Query handles all data loading automatically

  // Filtrage des paiements
  const filteredPayments = payments.filter(payment => {
    // Recherche par texte
    const matchesSearch = 
      (payment.reference ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (payment.student?.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (payment.course?.title ?? '').toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Filtrage par statut
    if (activeTab === 'all') return true;
    if (activeTab === 'paid') return payment.isPaid;
    if (activeTab === 'partial') return !payment.isPaid;
    if (activeTab === 'overdue') return isPaymentOverdue(payment);
    
    return true;
  });
  
  const paginatedPayments = filteredPayments.slice((currentPage - 1) * paymentsPerPage, currentPage * paymentsPerPage);
  const totalPayments = filteredPayments.length;
  const totalPages = Math.max(1, Math.ceil(totalPayments / paymentsPerPage));

  const formaLinkBtn = FORMA_LINK_BTN;

  const statsStrip = useMemo(() => {
    const paidTotal = payments.reduce((acc, payment) => {
      const paidAmount = parseFloat(payment.paidAmount) || parseFloat(payment.amount) || 0;
      return acc + paidAmount;
    }, 0);
    const outstandingTotal = payments.reduce((acc, payment) => {
      const totalAmount = parseFloat(payment.totalAmount) || parseFloat(payment.amount) || 0;
      const paidAmount = parseFloat(payment.paidAmount) || parseFloat(payment.amount) || 0;
      return acc + Math.max(0, totalAmount - paidAmount);
    }, 0);
    return [
      {
        title: t('paymentsPageUi.statTotalPaymentsTitle'),
        value: String(payments.length),
        hint: t('paymentsPageUi.statTotalPaymentsHint'),
        icon: DocumentTextIcon,
        iconWrap: 'bg-forma-500/15 text-forma-700 dark:bg-forma-500/20 dark:text-forma-200',
      },
      {
        title: t('paymentsPageUi.statAmountPaidTitle'),
        value: `${paidTotal.toLocaleString(dateLocale)} DA`,
        hint: t('paymentsPageUi.statAmountPaidHint'),
        icon: CurrencyDollarIcon,
        iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
      },
      {
        title: t('paymentsPageUi.statFullyPaidTitle'),
        value: String(payments.filter((p) => p.isPaid).length),
        hint: t('paymentsPageUi.statFullyPaidHint'),
        icon: CheckCircleIcon,
        iconWrap: 'bg-teal-500/15 text-teal-900 dark:bg-teal-500/20 dark:text-teal-100',
      },
      {
        title: t('paymentsPageUi.statOutstandingTitle'),
        value: `${outstandingTotal.toLocaleString(dateLocale)} DA`,
        hint: t('paymentsPageUi.statOutstandingOpenHint'),
        icon: ClockIcon,
        iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
      },
    ];
  }, [payments, t, dateLocale]);

  const pageNav = paginationItems(currentPage, totalPages);
  const pageSliceLabel =
    totalPayments === 0
      ? t('paymentsPageUi.emptyPaymentsHint')
      : t('paymentsPageUi.pageSliceShown', {
          start: Math.min((currentPage - 1) * paymentsPerPage + 1, totalPayments),
          end: Math.min(currentPage * paymentsPerPage, totalPayments),
          total: totalPayments,
        });

  // Multi-select functions
  const handleSelectPayment = (paymentId) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        // Remove from selection
        const newSelection = prev.filter(id => id !== paymentId);
        setIsSelectAll(false);
        return newSelection;
      } else {
        // Add to selection
        const newSelection = [...prev, paymentId];
        // Check if all visible payments are now selected
        setIsSelectAll(newSelection.length === paginatedPayments.length);
        return newSelection;
      }
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedPayments([]);
      setIsSelectAll(false);
    } else {
      // Select all visible payments
      const allPaymentIds = paginatedPayments.map(payment => payment.id);
      setSelectedPayments(allPaymentIds);
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedPayments];
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map((paymentId) => formationAPI.deleteFormationPayment(paymentId)));

      await invalidateFormationGraph(queryClient);

      setSelectedPayments([]);
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);

      toast.success(t('paymentsPageUi.toastBulkPaymentsDeletedOk', { n: ids.length }));
    } catch (error) {
      console.error('Error deleting payments:', error);
      toast.error(t('paymentsPageUi.toastBulkPaymentsDeleteFail'));
    }
  };

  // Update select all state when pagination changes
  React.useEffect(() => {
    if (selectedPayments.length === 0) {
      setIsSelectAll(false);
    } else {
      const visiblePaymentIds = paginatedPayments.map(p => p.id);
      const allVisibleSelected = visiblePaymentIds.every(id => selectedPayments.includes(id));
      setIsSelectAll(allVisibleSelected && visiblePaymentIds.length > 0);
    }
  }, [selectedPayments, paginatedPayments]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleDelete = async (paymentId) => {
    if (await confirmDialog(t('paymentsPageUi.confirmDeleteOnePayment'))) {
      try {
        await formationAPI.deleteFormationPayment(paymentId);
        await invalidateFormationGraph(queryClient);
        toast.success(t('paymentsPageUi.toastPaymentDeletedOk'));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error(t('paymentsPageUi.toastPaymentDeleteFail'));
      }
    }
  };

  // Helper function to find member for a payment
  const findMemberForPayment = (payment) => {
    let member = null;
    
    // Try to find member from API data first
    if (payment.member) {
      member = payment.member;
    } else if (payment.memberId && students.length > 0) {
      member = students.find(m => m.id === payment.memberId);
    } 
    // Try to find student by ID in students data
    else if (payment.studentId) {
      const student = students.find(s => s.id === payment.studentId);
      if (student) {
      member = {
          firstName: student.firstName || student.name?.split(' ')[0] || '',
          lastName: student.lastName || student.name?.split(' ').slice(1).join(' ') || '',
          memberCode: student.memberCode || student.code || student.studentCode || 'N/A'
        };
      }
    }
    // Fallback to stored student data
    else if (payment.student) {
      member = {
        firstName: payment.student.firstName || payment.student.name?.split(' ')[0] || '',
        lastName: payment.student.lastName || payment.student.name?.split(' ').slice(1).join(' ') || '',
        memberCode: payment.student.code || payment.student.memberCode || payment.student.studentCode || 'N/A'
      };
    }
    
    return member;
  };

  // Helper function to convert number to French words (used for receipt PDF text in FR)
  const numberToWords = (num) => {
    if (num === 0) return 'zéro';
    
    const ones = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
    const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
    const hundreds = 'cent';
    const thousands = 'mille';
    
    function convertHundreds(n) {
      let result = '';
      
      if (n >= 100) {
        const h = Math.floor(n / 100);
        if (h === 1) {
          result += hundreds;
    } else {
          result += ones[h] + ' ' + hundreds;
        }
        n %= 100;
        if (n > 0) result += ' ';
      }
      
      if (n >= 20) {
        const t = Math.floor(n / 10);
        result += tens[t];
        n %= 10;
        if (n > 0) {
          if (t === 8) result += '-' + ones[n];
          else result += '-' + ones[n];
        }
      } else if (n >= 10) {
        result += teens[n - 10];
      } else if (n > 0) {
        result += ones[n];
      }
      
      return result;
    }
    
    if (num >= 1000) {
      const t = Math.floor(num / 1000);
      let result = '';
      if (t === 1) {
        result += thousands;
      } else {
        result += convertHundreds(t) + ' ' + thousands;
      }
      num %= 1000;
      if (num > 0) {
        result += ' ' + convertHundreds(num);
      }
      return result;
    } else {
      return convertHundreds(num);
    }
  };

  // Direct PDF Export function
  const handleDirectPDFExport = async (payment) => {
    try {
      // Check if settings are still loading
      if (settingsLoading) {
        toast.error(t('paymentsPageUi.toastWaitSettingsLoad'));
        return;
      }

      const member = findMemberForPayment(payment);
      if (!member) {
        toast.error(t('paymentsPageUi.toastMemberInfoMissingPayment'));
        return;
      }

      console.log('Organization info:', organizationInfo);

      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');

      // Create A6 landscape receipt (148mm x 105mm)
      const doc = new jsPDF('l', 'mm', [105, 148]);
      const pageWidth = doc.internal.pageSize.width;

      // Set font
      doc.setFont('helvetica');

      // Organization info with safe fallbacks
      const orgInfo = {
        name: organizationInfo?.gymName || organizationInfo?.name || 'FORMAPRO',
        address: organizationInfo?.address || t('paymentsPageUi.orgFallbackAddress'),
        phone: organizationInfo?.phone || t('paymentsPageUi.phoneFallback'),
        logo: organizationInfo?.logo || null,
      };

      // Receipt data
      const receiptNumber = payment.receiptNumber || `REC-${Math.floor(Math.random() * 1000000)}`;
      const paymentDate = payment.date
        ? new Date(payment.date).toLocaleDateString(dateLocale)
        : new Date().toLocaleDateString(dateLocale);
      const paymentAmount = payment.amount || payment.paidAmount || payment.totalAmount || 0;
      const paymentNum = Number(paymentAmount) || 0;
      const amtLocale = paymentNum.toLocaleString(dateLocale);
      const amountInWords =
        language === 'fr'
          ? t('paymentsPageUi.pdfDinarsTail', { words: numberToWords(paymentNum) })
          : t('paymentsPageUi.pdfNumericAmountFallback', { amount: amtLocale });
      
      // Add logo if available
      let logoHeight = 0;
      if (orgInfo.logo) {
        try {
          // Determine image format from data URL
          let format = 'JPEG';
          if (orgInfo.logo.includes('data:image/png')) {
            format = 'PNG';
          } else if (orgInfo.logo.includes('data:image/jpeg') || orgInfo.logo.includes('data:image/jpg')) {
            format = 'JPEG';
          }
          
          doc.addImage(orgInfo.logo, format, 10, 8, 20, 20);
          logoHeight = 25;
        } catch (error) {
          console.warn('Could not add logo to PDF, continuing without logo:', error);
          // Continue without logo if it fails
        }
      }

      // Header for A6 landscape (adjust position based on logo)
      const headerY = logoHeight > 0 ? 15 : 15;
      const headerX = logoHeight > 0 ? 35 : 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(orgInfo.name, headerX, headerY);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(orgInfo.address, headerX, headerY + 5);
      doc.text(orgInfo.phone, headerX, headerY + 9);
      
      // Receipt number and date (top right)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblNum')} ${receiptNumber}`, pageWidth - 50, 15);
      doc.setFont('helvetica', 'normal');
      doc.text(`${t('paymentsPageUi.pdfLblDate')} ${paymentDate}`, pageWidth - 50, 19);
      
      // Separator line
      doc.setLineWidth(0.5);
      doc.line(10, 28, pageWidth - 10, 28);
      
      // Receipt title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(t('paymentsPageUi.pdfReceiptTitle'), pageWidth / 2, 40, { align: 'center' });

      const startY = 52;
      const lineHeight = 6;
      const leftColumn = 10;
      const rightColumn = pageWidth / 2 + 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblReceivedFrom')}`, leftColumn, startY);
      doc.setFont('helvetica', 'normal');
      doc.text(`${member?.firstName || ''} ${member?.lastName || ''}`, leftColumn, startY + lineHeight);

      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblFor')}`, leftColumn, startY + lineHeight * 3);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${payment.description || t('paymentsPageUi.pdfDescFormation')}`,
        leftColumn + 12,
        startY + lineHeight * 3,
        { maxWidth: 42 }
      );

      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblMethod')}`, leftColumn, startY + lineHeight * 5);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${localizedPaymentMethodLabel(undefined, payment.paymentMethod, t)}`,
        leftColumn + 20,
        startY + lineHeight * 5
      );

      if (payment.reference) {
        doc.setFont('helvetica', 'bold');
        doc.text(`${t('paymentsPageUi.pdfLblReference')}`, leftColumn, startY + lineHeight * 7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${payment.reference}`, leftColumn + 25, startY + lineHeight * 7);
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${t('paymentsPageUi.pdfLblAmount')} ${paymentNum.toLocaleString(dateLocale)} DA`,
        rightColumn,
        startY
      );

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblAmountWords')}`, rightColumn, startY + lineHeight * 2);
      doc.setFont('helvetica', 'normal');
      doc.text(`${amountInWords}`, rightColumn, startY + lineHeight * 3, { maxWidth: 45 });

      doc.save(`Recu_Paiement_${receiptNumber}.pdf`);
      toast.success(t('paymentsPageUi.pdfExportOk'));
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      toast.error(t('paymentsPageUi.toastPdfExportError'));
    }
  };

  // Direct Print function
  const handleDirectPrint = async (payment) => {
    try {
      if (settingsLoading) {
        toast.error(t('paymentsPageUi.toastWaitSettingsLoad'));
        return;
      }

      const member = findMemberForPayment(payment);
      if (!member) {
        toast.error(t('paymentsPageUi.toastMemberInfoMissingPayment'));
        return;
      }

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('l', 'mm', [105, 148]);
      const pageWidth = doc.internal.pageSize.width;

      doc.setFont('helvetica');

      const orgInfo = {
        name: organizationInfo?.gymName || organizationInfo?.name || 'FORMAPRO',
        address: organizationInfo?.address || t('paymentsPageUi.orgFallbackAddress'),
        phone: organizationInfo?.phone || t('paymentsPageUi.phoneFallback'),
        logo: organizationInfo?.logo || null,
      };

      const receiptNumber = payment.receiptNumber || `REC-${Math.floor(Math.random() * 1000000)}`;
      const paymentDate = payment.date
        ? new Date(payment.date).toLocaleDateString(dateLocale)
        : new Date().toLocaleDateString(dateLocale);
      const paymentAmount = payment.amount || payment.paidAmount || payment.totalAmount || 0;
      const paymentNum = Number(paymentAmount) || 0;
      const amtLocale = paymentNum.toLocaleString(dateLocale);
      const amountInWords =
        language === 'fr'
          ? t('paymentsPageUi.pdfDinarsTail', { words: numberToWords(paymentNum) })
          : t('paymentsPageUi.pdfNumericAmountFallback', { amount: amtLocale });

      let logoHeight = 0;
      if (orgInfo.logo) {
        try {
          let format = 'JPEG';
          if (orgInfo.logo.includes('data:image/png')) format = 'PNG';
          else if (orgInfo.logo.includes('data:image/jpeg') || orgInfo.logo.includes('data:image/jpg'))
            format = 'JPEG';
          doc.addImage(orgInfo.logo, format, 10, 8, 20, 20);
          logoHeight = 25;
        } catch (error) {
          console.warn('Could not add logo to PDF, continuing without logo:', error);
        }
      }

      const headerY = logoHeight > 0 ? 15 : 15;
      const headerX = logoHeight > 0 ? 35 : 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(orgInfo.name, headerX, headerY);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(orgInfo.address, headerX, headerY + 5);
      doc.text(orgInfo.phone, headerX, headerY + 9);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblNum')} ${receiptNumber}`, pageWidth - 50, 15);
      doc.setFont('helvetica', 'normal');
      doc.text(`${t('paymentsPageUi.pdfLblDate')} ${paymentDate}`, pageWidth - 50, 19);

      doc.setLineWidth(0.5);
      doc.line(10, 28, pageWidth - 10, 28);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(t('paymentsPageUi.pdfReceiptTitle'), pageWidth / 2, 40, { align: 'center' });

      const startY = 52;
      const lineHeight = 6;
      const leftColumn = 10;
      const rightColumn = pageWidth / 2 + 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblReceivedFrom')}`, leftColumn, startY);
      doc.setFont('helvetica', 'normal');
      doc.text(`${member?.firstName || ''} ${member?.lastName || ''}`, leftColumn, startY + lineHeight);

      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblFor')}`, leftColumn, startY + lineHeight * 3);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${payment.description || t('paymentsPageUi.pdfDescFormation')}`,
        leftColumn + 12,
        startY + lineHeight * 3,
        { maxWidth: 42 }
      );

      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblMethod')}`, leftColumn, startY + lineHeight * 5);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${localizedPaymentMethodLabel(undefined, payment.paymentMethod, t)}`,
        leftColumn + 20,
        startY + lineHeight * 5
      );

      if (payment.reference) {
        doc.setFont('helvetica', 'bold');
        doc.text(`${t('paymentsPageUi.pdfLblReference')}`, leftColumn, startY + lineHeight * 7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${payment.reference}`, leftColumn + 25, startY + lineHeight * 7);
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `${t('paymentsPageUi.pdfLblAmount')} ${paymentNum.toLocaleString(dateLocale)} DA`,
        rightColumn,
        startY
      );

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${t('paymentsPageUi.pdfLblAmountWords')}`, rightColumn, startY + lineHeight * 2);
      doc.setFont('helvetica', 'normal');
      doc.text(`${amountInWords}`, rightColumn, startY + lineHeight * 3, { maxWidth: 45 });
      
      // Create PDF blob and print it
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Create iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.top = '0';
      printFrame.style.left = '0';
      printFrame.style.width = '100%';
      printFrame.style.height = '100%';
      printFrame.style.zIndex = '9999';
      printFrame.style.backgroundColor = 'white';
      printFrame.src = pdfUrl;

      const printStyles = document.createElement('style');
      printStyles.id = 'pdf-print-styles';
      printStyles.innerHTML = `
        @media print {
          body * { visibility: hidden !important; }
          #pdf-print-frame {
            visibility: visible !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 9999 !important;
          }
        }
      `;
      printFrame.id = 'pdf-print-frame';
      
      document.head.appendChild(printStyles);
      document.body.appendChild(printFrame);
      
      // Wait for PDF to load, then show print dialog
      printFrame.onload = () => {
        setTimeout(() => {
          try {
            // Try to print the iframe content
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          } catch (error) {
            // Fallback: use window.print() which will print the iframe due to our CSS
            window.print();
          }
          
          // Cleanup after printing
          const cleanup = () => {
            if (document.body.contains(printFrame)) {
              document.body.removeChild(printFrame);
            }
            const existingStyles = document.getElementById('pdf-print-styles');
            if (existingStyles) {
              document.head.removeChild(existingStyles);
            }
            URL.revokeObjectURL(pdfUrl);
          };
          
          // Listen for after print to cleanup
          const afterPrint = () => {
            cleanup();
            const closeButton = document.getElementById('close-print-btn');
            if (closeButton && document.body.contains(closeButton)) {
              document.body.removeChild(closeButton);
            }
            window.removeEventListener('afterprint', afterPrint);
          };
          
          window.addEventListener('afterprint', afterPrint);
          
          // Add close button to iframe
          const closeButton = document.createElement('button');
          closeButton.id = 'close-print-btn';
          closeButton.innerHTML = t('paymentsPageUi.printCloseBtn');
          closeButton.style.position = 'fixed';
          closeButton.style.top = '20px';
          closeButton.style.right = '20px';
          closeButton.style.zIndex = '10000';
          closeButton.style.padding = '10px 15px';
          closeButton.style.backgroundColor = '#ff4444';
          closeButton.style.color = 'white';
          closeButton.style.border = 'none';
          closeButton.style.borderRadius = '5px';
          closeButton.style.cursor = 'pointer';
          closeButton.style.fontSize = '14px';
          closeButton.onclick = () => {
            cleanup();
            if (document.body.contains(closeButton)) {
              document.body.removeChild(closeButton);
            }
          };
          
          document.body.appendChild(closeButton);
          
          // Extended fallback cleanup (30 seconds)
          setTimeout(() => {
            if (document.body.contains(printFrame)) {
              cleanup();
              if (document.body.contains(closeButton)) {
                document.body.removeChild(closeButton);
              }
            }
          }, 30000);
        }, 1000);
      };
      
      // Handle iframe load error
      printFrame.onerror = () => {
        console.error('Failed to load PDF in iframe');
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
        const existingStyles = document.getElementById('pdf-print-styles');
        if (existingStyles) {
          document.head.removeChild(existingStyles);
        }
        URL.revokeObjectURL(pdfUrl);
        toast.error(t('paymentsPageUi.toastPrintIframeError'));
      };
      
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      toast.error(t('paymentsPageUi.toastPrintError'));
    }
  };

  return (
    <div>
      <Routes>
      <Route index element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <PageHeader
              title={t('paymentsPageTitle')}
              description={t('pageDescriptions.payments')}
              actions={
                <Link to="/payments/add" className={formaLinkBtn}>
                <PlusIcon className="h-5 w-5" />
                  <span>{t('paymentsPageUi.newPaymentBtn')}</span>
              </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
                  {statsStrip.map((stat, index) => (
              <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.05 }}
                      whileHover={{ y: -2 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-3">
                          <div
                            className={clsx(
                              'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-5 w-5" />
                  </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white sm:text-2xl">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                    </p>
                  </div>
                  </div>
                </div>
              </motion.div>
                  ))}
                </div>
            </div>

              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                  <div className="relative min-w-0 flex-1 sm:min-w-[14rem]">
                    <label htmlFor="payment-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('paymentsPageUi.searchPaymentsSr')}
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute bottom-2.5 left-3 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="payment-search"
                      type="search"
                      autoComplete="off"
                      placeholder={t('paymentsPageUi.searchPlaceholderPayments')}
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                        searchTerm.trim() ? 'pr-10' : 'pr-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label={t('paymentsPageUi.clearSearchAria')}
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute bottom-1.5 right-2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                  <FilterSelect
                    id="payment-status-filter"
                    label={t('paymentsPageUi.lblStatusPayment')}
                    value={activeTab}
                    onChange={(v) => {
                      setActiveTab(v);
                      setCurrentPage(1);
                    }}
                    options={[
                      { id: 'all', name: t('paymentsPageUi.tabAll'), count: payments.length },
                      { id: 'paid', name: t('paymentsPageUi.tabPaidShort'), count: payments.filter((p) => p.isPaid).length },
                      { id: 'partial', name: t('paymentsPageUi.tabPartialShort'), count: payments.filter((p) => !p.isPaid).length },
                      {
                        id: 'overdue',
                        name: t('paymentsPageUi.tabOverdueShort'),
                        count: payments.filter((p) => isPaymentOverdue(p)).length,
                      },
                    ]}
                    className="sm:min-w-[11rem]"
                  />
                </div>
              </div>

              {(searchTerm.trim() !== '' || activeTab !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    {t('paymentsPageUi.activeFiltersLbl')}
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {activeTab !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {activeTab === 'paid'
                        ? t('paymentsPageUi.tabPaidOnly')
                        : activeTab === 'overdue'
                          ? t('paymentsPageUi.tabOverdueOnly')
                          : t('paymentsPageUi.tabPartialOnly')}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveTab('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    {t('paymentsPageUi.resetFiltersBtn')}
                  </button>
                    </div>
              )}
            </UiCard>

            <UiCard padding={false} className="overflow-hidden">
              {selectedPayments.length > 0 ? (
                <div className="border-b border-forma-200/60 bg-forma-50/60 px-5 py-4 dark:border-forma-900/40 dark:bg-forma-950/30">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {t('paymentsPageUi.bulkSelectedPayments', { n: selectedPayments.length })}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                      onClick={() => {
                        setSelectedPayments([]);
                        setIsSelectAll(false);
                      }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                        {t('paymentsPageUi.deselectAllBtn')}
                    </button>
                    <button
                        type="button"
                      onClick={() => setShowBulkDeleteModal(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
                    >
                      <TrashIcon className="h-4 w-4" />
                      {t('paymentsPageUi.deleteBulkBtn', { n: selectedPayments.length })}
                    </button>
                  </div>
                </div>
              </div>
              ) : null}

              <div className="flex flex-col gap-1 border-b border-slate-200 bg-white/50 px-5 py-3 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {t('paymentsPageUi.paymentListHeading')}
                </p>
                <p className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{pageSliceLabel}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur dark:bg-slate-800/95">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={isSelectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colReferenceShort')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colStudentShort')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colCourseTbl')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colDateTbl')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colDueDateTbl')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colAmountTbl')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colStatusTbl')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('paymentsPageUi.colActionsTbl')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {paginatedPayments.map((payment, rowIdx) => (
                      <tr
                        key={payment.id}
                        className={clsx(
                          'transition-colors',
                          rowIdx % 2 === 0 ? 'bg-white/90 dark:bg-slate-950/35' : 'bg-slate-50/80 dark:bg-slate-900/45',
                          'hover:bg-forma-50/80 dark:hover:bg-forma-950/25'
                        )}
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedPayments.includes(payment.id)}
                            onChange={() => handleSelectPayment(payment.id)}
                            className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center">
                            <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-forma-500/15">
                              <DocumentTextIcon className="h-4 w-4 text-forma-700 dark:text-forma-300" />
                            </div>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                              {payment.invoiceNumber || payment.reference}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                            {getStudentName(payment)}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400">
                            {payment.studentId ||
                              payment.memberId ||
                              payment.member?.memberCode ||
                              payment.student?.code ||
                              t('paymentsPageUi.undef')}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm text-slate-900 dark:text-slate-50">
                            {payment.description ||
                              payment.course?.title ||
                              t('paymentsPageUi.noCourseChosen')}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm text-slate-900 dark:text-slate-50">
                            {new Date(payment.paymentDate || payment.date).toLocaleDateString(dateLocale)}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {!payment.isPaid && payment.dueDate ? (
                            <div>
                              <div
                                className={clsx(
                                  'text-sm',
                                  isPaymentOverdue(payment)
                                    ? 'font-medium text-red-600 dark:text-red-300'
                                    : 'text-slate-900 dark:text-slate-50'
                                )}
                              >
                                {new Date(payment.dueDate).toLocaleDateString(dateLocale)}
                              </div>
                              {isPaymentOverdue(payment) ? (
                                <div className="mt-0.5 text-xs font-medium text-red-500 dark:text-red-400">
                                  {t('paymentsPageUi.overdueBadge')}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="text-sm text-slate-900 dark:text-slate-50">
                            {(parseFloat(payment.amount) || parseFloat(payment.paidAmount) || 0).toLocaleString(dateLocale)}{' '}
                            / {(parseFloat(payment.amount) || parseFloat(payment.totalAmount) || 0).toLocaleString(dateLocale)} DA
                          </div>
                          {!payment.isPaid && (
                            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200/90 dark:bg-slate-800/80">
                              <div
                                className="h-full rounded-full bg-forma-500"
                                style={{
                                  width: `${((parseFloat(payment.paidAmount) || 0) / (parseFloat(payment.totalAmount) || 1)) * 100}%`,
                                }}
                              />
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={clsx(
                              'rounded-full px-2 py-1 text-xs',
                              payment.isPaid || payment.status === 'paid'
                                ? 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
                                : isPaymentOverdue(payment)
                                  ? 'bg-red-500/15 text-red-800 dark:text-red-200'
                                  : 'bg-amber-500/15 text-amber-900 dark:text-amber-100'
                            )}
                          >
                            {payment.isPaid || payment.status === 'paid'
                              ? t('paymentsPageUi.paidLbl')
                              : isPaymentOverdue(payment)
                                ? t('paymentsPageUi.overdueBadge')
                                : t('paymentsPageUi.badgePartialSuffix')}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <Link
                              to={`/payments/${payment.id}`}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title={t('paymentsPageUi.titleViewPayment')}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                            <Link
                              to={`/payments/edit/${payment.id}`}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title={t('paymentsPageUi.titleEditPayment')}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleDirectPDFExport(payment)}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title={t('paymentsPageUi.titleExportPdf')}
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDirectPrint(payment)}
                              className="rounded-lg bg-slate-100 p-1.5 text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              title={t('paymentsPageUi.titlePrintReceipt')}
                            >
                              <PrinterIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(payment.id)}
                              className="rounded-lg bg-red-500/10 p-1.5 text-red-600 transition hover:bg-red-500/20 dark:text-red-400"
                              title={t('paymentsPageUi.titleDeletePayment')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPayments > paymentsPerPage ? (
                <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-600 dark:text-slate-400">{pageSliceLabel}</p>
                  <nav
                    className="flex flex-wrap items-center justify-end gap-2"
                    aria-label={t('paymentsPageUi.paginationAriaLabel')}
                  >
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                      {t('paymentsPageUi.paginationPrev')}
                    </button>
                    <div className="flex flex-wrap items-center gap-1">
                      {pageNav.map((node) =>
                        node.type === 'ellipsis' ? (
                          <span key={node.key} className="px-2 py-1 text-sm font-medium text-slate-400" aria-hidden>
                            …
                          </span>
                        ) : (
                    <button
                            key={node.page}
                            type="button"
                            onClick={() => setCurrentPage(node.page)}
                            className={clsx(
                              'min-w-[2.25rem] rounded-lg px-2 py-1 text-sm font-semibold transition',
                              currentPage === node.page
                                ? 'bg-forma-600 text-white shadow-sm'
                                : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                            )}
                          >
                            {node.page}
                          </button>
                        )
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      {t('paymentsPageUi.paginationNext')}
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    </button>
                  </nav>
                </div>
              ) : null}
            </UiCard>
          </motion.div>
        }
      />
      <Route
        path="add"
        element={
          <FormPageLayout backTo="/payments" backLabel={t('paymentsPageUi.backToPayments')}>
            <PaymentForm students={students} courses={courses} existingPayments={payments} />
          </FormPageLayout>
        }
      />
      <Route
        path="edit/:id"
        element={
          <FormPageLayout backTo="/payments" backLabel={t('paymentsPageUi.backToPayments')}>
            <PaymentEditRoute
              payments={payments}
              students={students}
              courses={courses}
            />
          </FormPageLayout>
        }
      />
      <Route
        path=":id"
        element={<PaymentDetailRoute payments={payments} students={students} />}
      />
      </Routes>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <TrashIcon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">
                {t('paymentsPageUi.bulkDeleteTitlePayments')}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {t('paymentsPageUi.bulkDeleteAskPayments', { n: selectedPayments.length })}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors"
                >
                  {t('paymentsPageUi.cancelBtnPayments')}
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('paymentsPageUi.deleteBtnPayments')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage; 